import React, { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import useBackendApi from '../hooks/useBackendApi';
import OrderDetailsContent from './OrderDetailsContent';
import { OrderData as OrderDataType, TestKitDataType } from '../types/orderTypes';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { RequestOptions } from '../types/httpRequests';

const backendURL = process.env.REACT_APP_BACKEND_URL;

const OrderDetailsPage = () => {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string; id: string }>();
  const [orderData, setOrderData] = useState<OrderDataType | null>(null);
  const [testKits, setTestKits] = useState<TestKitDataType[]>([]);
  const { isLoading, apiFetchRequest } = useBackendApi();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const navigate = useNavigate();
  const [isAuthStatusReady, setIsAuthStatusReady] = useState(false);

  useEffect(() => {
    if (authStatus !== 'configuring') {
      setIsAuthStatusReady(true);
    }

    if (!isAuthStatusReady) {
      return;
    }

    // We give the guest user a chance to access the order details
    if (authStatus === 'unauthenticated') {
      return navigate(`/guest-orders/${shopifyOrderId}`);
    }

    const apiRequestOptions: RequestOptions = {
      endpoint: `${backendURL}/orders/${shopifyOrderId}`,
    };

    const testkitRequestOptions: RequestOptions = {
      endpoint: `${backendURL}/orders/${shopifyOrderId}/test_kits`,
    };

    const fetchData = async () => {
      try {
        const [orderData, testkitsData] = await Promise.all([
          apiFetchRequest(apiRequestOptions),
          apiFetchRequest(testkitRequestOptions),
        ]);

        if (orderData.status !== 200) {
          return navigate('/404.html');
        }

        setTestKits(testkitsData.body?.test_kits || []);
        setOrderData(orderData.body);
      } catch (err) {
        console.error('Error fetching order', err);
      }
    };

    fetchData();
  }, [authStatus, isAuthStatusReady]);

  if (isLoading || !orderData) {
    return <GradientCircularProgressContainer />;
  }

  return <OrderDetailsContent orderData={orderData} testKitsData={testKits} />;
};

export default OrderDetailsPage;
